import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/lib/auth';
import {
  fetchNotifications,
  markNotificationRead,
  takeNotificationAction,
} from '@/lib/notifications';

export function useNotifications() {
  const queryClient = useQueryClient();
  const auth = useAuth();
  const queryKey = ['notifications', auth.user?.id ?? 'anonymous'] as const;

  const query = useQuery({
    queryKey,
    queryFn: fetchNotifications,
    enabled: auth.isAuthenticated,
    retry: false,
  });

  const markReadMutation = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const actionMutation = useMutation({
    mutationFn: ({
      notificationId,
      action,
    }: {
      notificationId: string;
      action: 'approve' | 'remove';
    }) => takeNotificationAction(notificationId, action),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['submissions'] });
      queryClient.invalidateQueries({ queryKey: ['challenges'] });
    },
  });

  const notifications = query.data ?? [];
  const unreadCount = notifications.filter((notification) => !notification.readAt).length;

  return {
    notifications,
    unreadCount,
    isLoading: auth.isLoading || query.isLoading,
    isError: query.isError,
    error: query.error ?? markReadMutation.error ?? actionMutation.error,
    markRead: markReadMutation.mutate,
    takeAction: (notificationId: string, action: 'approve' | 'remove') =>
      actionMutation.mutate({ notificationId, action }),
    isTakingAction: actionMutation.isPending,
  };
}
